import { FC, ForwardRefRenderFunction, ReactNode, forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import IcChevronLeftBold from 'src/assets/images/icChevronLeftBold.svg';
import IcCloseBold from 'src/assets/images/icCloseBold.svg';
import LoadingIndicator from 'src/components/LoadingIndicator';
import { ModalBackground, ModalKeyContext, Props as ModalProps } from 'src/components/Modal';
import { ModalContentContext, ModalContentProvider } from 'src/context/ModalContext/ModalContentContext';
import useIntersection from 'src/hooks/useIntersection';
import { closeModalAtom, openedModalsAtom } from 'src/stores/modal/atoms';
import { fadeInTop } from 'src/styles/animation';
import screenSize from 'src/styles/screenSize';
import { ModalNavButtonType } from 'src/types/Modal';
import { DataAttributes } from 'src/types/utils';
import { hexToRgb } from 'src/utils/common';
import { ModalScrollRoot } from './ModalScrollComponent';
type ScreenSizes = Extract<keyof typeof screenSize, 'mobile' | 'tablet' | 'desktop'>;
export const enum MobileModalType {
  BOTTOM_SHEET = 'bottomSheet',
  SCREEN_MODAL = 'screenModal',
}

/**
 * 모바일에서는 닫기 버튼과 뒤로가기 버튼을 동시 제공하지 않음
 */
export type MobileModalNavButtonType = Exclude<ModalNavButtonType, ModalNavButtonType.BACK_AND_CLOSE>;
export const screenModal = css`
  flex-wrap: wrap;
  --modal-border-radius-top: 0px;
  --modal-border-radius-bottom: 0px;
  overflow: scroll;
  flex-direction: column;
  width: var(--100vw);
  height: 100%;
  bottom: 0;
  position: absolute;
`;
export const ModalInner = styled.div<{
  mobileType: MobileModalType;
}>`
  width: 100%;
  height: 100%;
  ${({
  mobileType
}) => mobileType === MobileModalType.BOTTOM_SHEET && `
    max-height: calc(85 * var(--dvh));
  `}
  display: flex;
  flex-direction: column;
`;
export const bottomModal = css`
  width: var(--100vw);
  position: fixed;
  bottom: 0px;
  animation: ${fadeInTop} 0.4s ease-in-out;
  --modal-border-radius-bottom: 0px;
`;
export const ModalRootStyle = styled.div<{
  mobileType: MobileModalType;
  mobileBreakdpoint: ScreenSizes;
  isAndroidWebview?: boolean;
}>`
  --modal-border-radius-top: 20px;
  --modal-border-radius-bottom: 20px;
  border-top-left-radius: var(--modal-border-radius-top);
  border-top-right-radius: var(--modal-border-radius-top);
  border-bottom-left-radius: var(--modal-border-radius-bottom);
  border-bottom-right-radius: var(--modal-border-radius-bottom);
  --background-color: ${({
  theme
}) => theme.color.bg2};
  ${({
  theme
}) => theme.screenSize.mobile} {
    --background-color: ${({
  theme,
  mobileType
}) => mobileType === MobileModalType.SCREEN_MODAL ? theme.color.bg1 : theme.color.bg2};
  }
  --nav-button-color: ${({
  theme
}) => theme.color.contentDeep};
  background-color: var(--background-color);
  min-width: 400px;
  position: relative;
  box-shadow: 0px 2px 20px rgba(${({
  theme
}) => hexToRgb(theme.color.surfaceDeepFixed)}, 0.2);
  max-width: 700px;
  bottom: 0;
  color: ${({
  theme
}) => theme.color.content9};

  ${({
  theme
}) => theme.screenSize.tablet} {
    max-width: 500px;
  }

  ${({
  theme,
  mobileBreakdpoint
}) => theme.screenSize[mobileBreakdpoint]} {
    ${({
  mobileType
}) => mobileType === MobileModalType.BOTTOM_SHEET ? bottomModal : screenModal}
    max-width: 100%;
    min-width: auto;
    /* PWA/웹 및 iOS 웹뷰에서 홈 인디케이터와 겹치지 않도록 하단 safe-area 패딩 추가 (Android 웹뷰 제외) */
    ${({
  isAndroidWebview
}) => !isAndroidWebview && `padding-bottom: var(--sab);`}
  }
`;
const ButtonIconStyle = ({
  theme
}: {
  theme: Theme;
}) => css`
  ${theme.isRtl && `
    transform: scale(-1);
  `}
`;
export const ButtonIcon = styled.img`
  ${ButtonIconStyle}
`;
const CloseButtonIcon = styled(IcCloseBold)`
  ${ButtonIconStyle}
`;
const BackButtonIcon = styled(IcChevronLeftBold)`
  ${ButtonIconStyle}
`;
const ModalNavButton = styled.button<{
  buttonType: ModalNavButtonType.CLOSE | ModalNavButtonType.BACK;
}>`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;

  position: absolute;
  background-color: transparent;
  color: var(--nav-button-color);
  z-index: 2;
  justify-content: center;
  align-items: center;
  display: flex;
  height: 40px;
  width: 40px;
  ${({
  buttonType
}) => `
    inset-inline-${buttonType === ModalNavButtonType.BACK ? 'start' : 'end'}: 16px;
  `}
  top: 16px;
  padding: 8px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    // 모바일에서는 단일 Nav 버튼만 제공하는 대신 위치를 항상 고정
    inset-inline-start: 16px;
    inset-inline-end: auto;
  }

  & > svg {
    width: 24px;
    height: 24px;
  }
`;

// Dim을 위한 컨테이너
export const ModalContentContainer = styled.div`
  position: relative;
  max-width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  display: flex;
`;

// max-height 주어서 컨텐츠 높이조절()
export const ModalContentStyle = styled(ModalScrollRoot)`
  padding-left: 24px;
  padding-right: 24px;
  max-width: 100%;
  flex: 1;
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  resize: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;
export const ModalTitleContainer = styled.div`
  z-index: 1;
  padding: 24px 24px;
  gap: 8px;
  display: flex;
  width: 100%;
  flex-direction: column;
`;
export const ModalTitle = styled.p`
  font-size: 30px;
  font-weight: 700;
  color: ${({
  theme
}) => theme.color.contentDeep};
  margin: 0px;
  line-height: 40px;
`;
export const ModalTitleDescription = styled.div`
  font-size: 14px;
  color: ${({
  theme
}) => theme.color.content8};
  font-weight: 400;
  line-height: 18px;
`;
export const ModalDim = (isShow: boolean) => css`
  position: absolute;
  pointer-events: none;
  width: 100%;
  left: 0px;
  right: 0px;
  transition: opacity 0.1s;
  opacity: ${isShow ? 1 : 0};
  z-index: 1;
`;
export const ModalHeaderTitle = styled.p<{
  mobileType: MobileModalType;
  buttonType?: ModalNavButtonType;
}>`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 28px;
  color: ${({
  theme
}) => theme.color.contentDeep};
  word-break: keep-all;
  word-wrap: break-word;
  &:lang(ja) {
    word-break: break-word;
    overflow-wrap: anywhere;
    line-break: anywhere;
  }
  ${({
  buttonType
}) => {
  if (!buttonType || buttonType === ModalNavButtonType.NONE) return 'padding: 0;';
  if (buttonType === ModalNavButtonType.BACK) return 'padding-inline-start: 32px;';
  if (buttonType === ModalNavButtonType.CLOSE) return 'padding-inline-end: 32px;';
  if (buttonType === ModalNavButtonType.BACK_AND_CLOSE) return 'padding-inline: 32px;';
  return 'padding: 0;';
}}
  ${({
  theme
}) => theme.screenSize.mobile} {
    ${({
  mobileType
}) => mobileType === MobileModalType.SCREEN_MODAL ? 'padding-inline: 32px;' : 'padding: 0;'}
  }
`;
export const ModalHeaderDescriptionRow = styled.div`
  font-size: 14px;
  color: ${({
  theme
}) => theme.color.content9};
  line-height: 18px;
`;
type ModalLoadingType = 'absolute' | 'relative';
const LoadingBackground = styled.div<{
  type: ModalLoadingType;
}>`
  ${({
  type,
  theme
}) => type === 'absolute' ? `
    position: absolute;
    height: 100%;
    background: #000000;
    opacity: 0.5;
    top: 0;
    ${theme.isRtl ? 'right' : 'left'}: 0;
    ` : `
    min-height: 178px;
  `}

  width: 100%;
  flex-direction: row;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top-left-radius: var(--modal-border-radius-top);
  border-top-right-radius: var(--modal-border-radius-top);
  border-bottom-left-radius: var(--modal-border-radius-bottom);
  border-bottom-right-radius: var(--modal-border-radius-bottom);
  // 모달헤더보다 위로 설정
  z-index: 2;
`;

// intersection observer 트리거하기 위해 1px 사용
const HeaderDimTrigger = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: -1px;
  z-index: -1;
`;
const FooterDimTrigger = styled.div`
  width: 100%;
  height: 1px;
  margin-top: -1px;
  z-index: -1;
`;

// Dim 컴포넌트들을 ModalContent에서 관리
export const HeaderDim = styled.div<{
  isShow: boolean;
}>`
  ${({
  isShow
}) => ModalDim(isShow)}
  height: 24px;
  top: 0;
  z-index: 1;
  border-top-left-radius: var(--modal-border-radius-top);
  border-top-right-radius: var(--modal-border-radius-top);
  background: linear-gradient(
    180deg,
    var(--background-color) 0%,
    rgba(${({
  theme
}) => hexToRgb(theme.color.surface12Fixed)}, 0) 100%
  );
`;
export const FooterDim = styled.div<{
  isShow: boolean;
}>`
  ${({
  isShow
}) => ModalDim(isShow)}
  height: 40px;
  bottom: 0;
  z-index: 1;
  border-bottom-left-radius: var(--modal-border-radius-bottom);
  border-bottom-right-radius: var(--modal-border-radius-bottom);
  background: linear-gradient(
    180deg,
    rgba(${({
  theme
}) => hexToRgb(theme.color.surface12Fixed)}, 0) 0%,
    var(--background-color) 100%
  );
`;
type ModalNavButtonProps = Pick<React.HTMLAttributes<HTMLButtonElement>, 'onClick' | 'className'>;
export const ModalCloseButton: FC<ModalNavButtonProps> = ({
  onClick,
  className
}) => {
  return <ModalNavButton className={className} onClick={onClick} data-testid='close-modal' buttonType={ModalNavButtonType.CLOSE} data-sentry-element="ModalNavButton" data-sentry-component="ModalCloseButton" data-sentry-source-file="index.tsx">
      <CloseButtonIcon data-sentry-element="CloseButtonIcon" data-sentry-source-file="index.tsx" />
    </ModalNavButton>;
};
export const ModalBackButton: FC<ModalNavButtonProps> = ({
  onClick,
  className
}) => {
  return <ModalNavButton onClick={onClick} className={className} data-testid='back-modal' buttonType={ModalNavButtonType.BACK} data-sentry-element="ModalNavButton" data-sentry-component="ModalBackButton" data-sentry-source-file="index.tsx">
      <BackButtonIcon data-sentry-element="BackButtonIcon" data-sentry-source-file="index.tsx" />
    </ModalNavButton>;
};
export const ModalLoading: FC<{
  type?: ModalLoadingType;
  className?: string;
}> = ({
  type = 'relative',
  ...props
}) => <LoadingBackground type={type} {...props} data-sentry-element="LoadingBackground" data-sentry-component="ModalLoading" data-sentry-source-file="index.tsx">
    <LoadingIndicator size={18} data-sentry-element="LoadingIndicator" data-sentry-source-file="index.tsx" />
  </LoadingBackground>;
export interface ModalLayoutProps extends Pick<React.HTMLAttributes<HTMLDivElement>, 'className' | 'style'>, React.PropsWithChildren, DataAttributes, ModalProps {
  onClose?: () => void;
  onBack?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onAnimationEnd?: () => void;
  mobileType?: MobileModalType;
  navButtonType?: ModalNavButtonType;
  mobileNavButtonType?: MobileModalNavButtonType;
  mobileBreakpoint?: ScreenSizes;
  lockBodyScroll?: boolean;
  closeWhenClickOutside?: boolean;
  closeWithESC?: boolean;
  children?: ReactNode;
}
interface ModalContentProps extends Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>, React.PropsWithChildren {}
export const ModalContent: FC<ModalContentProps> = ({
  children,
  ...props
}) => {
  const {
    setIsBottomReached,
    setIsTopReached,
    isBottomReached,
    isTopReached
  } = useContext(ModalContentContext);
  const handleHeaderIntersection = useCallback<IntersectionObserverCallback>(([entry]) => {
    setIsTopReached(entry.isIntersecting);
  }, [setIsTopReached]);
  const handleFooterIntersection = useCallback<IntersectionObserverCallback>(([entry]) => {
    setIsBottomReached(entry.isIntersecting);
  }, [setIsBottomReached]);
  const {
    observerRef: headerObserverRef
  } = useIntersection(handleHeaderIntersection);
  const {
    observerRef: footerObserverRef
  } = useIntersection(handleFooterIntersection);
  return <ModalContentContainer data-sentry-element="ModalContentContainer" data-sentry-component="ModalContent" data-sentry-source-file="index.tsx">
      <ModalContentStyle {...props} data-sentry-element="ModalContentStyle" data-sentry-source-file="index.tsx">
        <HeaderDimTrigger ref={headerObserverRef} data-sentry-element="HeaderDimTrigger" data-sentry-source-file="index.tsx" />
        {children}
        <FooterDimTrigger ref={footerObserverRef} data-sentry-element="FooterDimTrigger" data-sentry-source-file="index.tsx" />
      </ModalContentStyle>
      <HeaderDim isShow={!isTopReached} data-sentry-element="HeaderDim" data-sentry-source-file="index.tsx" />
      <FooterDim isShow={!isBottomReached} data-sentry-element="FooterDim" data-sentry-source-file="index.tsx" />
    </ModalContentContainer>;
};
const DesktopOnlyWrapper = styled.div`
  ${({
  theme
}) => theme.screenSize.mobile} {
    display: none;
  }
`;
const MobileOnlyWrapper = styled.div`
  display: none;
  ${({
  theme
}) => theme.screenSize.mobile} {
    display: block;
  }
`;
const ModalLayoutWithoutRef: ForwardRefRenderFunction<HTMLDivElement, ModalLayoutProps> = ({
  children,
  className,
  style,
  onBack,
  onClose,
  isDim = false,
  closeWhenClickOutside = true,
  closeWithESC = true,
  mobileType = MobileModalType.SCREEN_MODAL,
  mobileBreakpoint = 'mobile',
  navButtonType = ModalNavButtonType.NONE,
  mobileNavButtonType: mobileNavButtonTypeProp,
  lockBodyScroll = true,
  onAnimationEnd,
  ...props
}, ref) => {
  const closeModal = useSetAtom(closeModalAtom);
  const modalType = useContext(ModalKeyContext);
  const [isClient, setIsClient] = useState(false);
  const isAndroidWebview = isClient && window.AzarJs?.getPlatform?.() === 'ANDROID';
  useEffect(() => {
    setIsClient(true);
  }, []);

  // 모바일의 경우 분명하게 명시되지 않은 경우 모바일 모델 UI에 따라 기본값 결정
  const mobileNavButtonType = mobileNavButtonTypeProp ? mobileNavButtonTypeProp : mobileType === MobileModalType.SCREEN_MODAL ? ModalNavButtonType.CLOSE : ModalNavButtonType.NONE;
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    if (modalType) {
      closeModal(modalType);
    }
  };
  const setOpenModals = useSetAtom(openedModalsAtom);
  useEffect(() => {
    if (!closeWithESC) return;
    const handleNextLive = (e: KeyboardEvent) => {
      if ((e.target as HTMLInputElement)?.value) {
        return;
      }
      e.stopPropagation();
      if (e.key === 'Escape' || e.code === 'Escape') {
        setOpenModals(prev => prev.splice(0, prev.length - 1));
        onClose?.();
      }
    };
    window.addEventListener('keyup', handleNextLive, {
      capture: true
    });
    return () => {
      window.removeEventListener('keyup', handleNextLive, {
        capture: true
      });
    };
  }, [onClose, setOpenModals, closeWithESC]);
  const getNavButton = (buttonType: ModalNavButtonType) => {
    switch (buttonType) {
      case ModalNavButtonType.BACK:
        return <ModalBackButton onClick={onBack} />;
      case ModalNavButtonType.BACK_AND_CLOSE:
        return <>
            <ModalBackButton onClick={onBack} />
            <ModalCloseButton onClick={handleClose} />
          </>;
      case ModalNavButtonType.CLOSE:
        return <ModalCloseButton onClick={handleClose} />;
      default:
        return null;
    }
  };
  return <ModalBackground isDim={isDim} lockBodyScroll={lockBodyScroll} onClose={onClose} closeWhenClickOutside={closeWhenClickOutside} data-sentry-element="ModalBackground" data-sentry-component="ModalLayoutWithoutRef" data-sentry-source-file="index.tsx">
      <ModalContentProvider lockBodyScroll={lockBodyScroll} data-sentry-element="ModalContentProvider" data-sentry-source-file="index.tsx">
        <ModalRootStyle ref={ref} className={className} style={style} mobileType={mobileType} mobileBreakdpoint={mobileBreakpoint} isAndroidWebview={isAndroidWebview} onAnimationEnd={onAnimationEnd} {...props} data-sentry-element="ModalRootStyle" data-sentry-source-file="index.tsx">
          <ModalInner mobileType={mobileType} data-testid='Modal-Inner' data-sentry-element="ModalInner" data-sentry-source-file="index.tsx">
            <DesktopOnlyWrapper data-sentry-element="DesktopOnlyWrapper" data-sentry-source-file="index.tsx">{getNavButton(navButtonType)}</DesktopOnlyWrapper>
            <MobileOnlyWrapper data-sentry-element="MobileOnlyWrapper" data-sentry-source-file="index.tsx">{getNavButton(mobileNavButtonType)}</MobileOnlyWrapper>
            {children}
          </ModalInner>
        </ModalRootStyle>
      </ModalContentProvider>
    </ModalBackground>;
};
const ModalLayout = forwardRef(ModalLayoutWithoutRef);
export default ModalLayout;
export { default as ModalHeader } from './ModalHeader';
export { default as ModalFooter } from './ModalFooter';